import React, { useState, useEffect } from 'react';
import { 
  CurrencyDollarIcon,
  ChartBarIcon,
  DocumentArrowDownIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import apiClient from '../../utils/api';

interface FinancialSummary {
  total_income: number;
  total_expenses: number;
  total_location_expenses: number;
  gross_profit: number;
  net_profit: number;
  profit_margin: number;
  roi: number;
}

interface ExpenseBreakdown {
  expense_type: 'feed' | 'medicine' | 'salary' | 'maintenance' | 'diesel' | 'construction' | 'misc';
  total_amount: number;
  percentage: number;
  pond_breakdown: Array<{
    pond_id: string;
    pond_name: string;
    amount: number;
  }>;
}

interface ShedProfitability {
  shed_id: string;
  shed_name: string;
  total_income: number;
  total_expenses: number;
  allocated_costs: number;
  direct_costs: number;
  allocated_location_costs: number;
  total_location_expenses: number;
  allocation_proportion: number;
  net_profit: number;
  profit_margin: number;
  roi: number;
  goat_count: number;
  cost_per_goat: number;
  revenue_per_goat: number;
}

interface GoatFinancialReport {
  location_id: string;
  location_name: string;
  period_start: string;
  period_end: string;
  summary: FinancialSummary;
  expense_breakdown: ExpenseBreakdown[];
  shed_profitability: ShedProfitability[];
  monthly_trends: Array<{
    month: string;
    income: number;
    expenses: number;
    profit: number;
  }>;
}

const GoatFinancialReports: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [report, setReport] = useState<GoatFinancialReport | null>(null);

  useEffect(() => {
    // Set default date range (January 1st of current year to today)
    const today = new Date();
    const currentYear = today.getFullYear();
    
    // Create January 1st in IST (UTC+5:30)
    const januaryFirst = new Date(`${currentYear}-01-01T00:00:00+05:30`);
    
    // Format dates for input fields (YYYY-MM-DD)
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    setEndDate(formatDate(today));
    setStartDate(formatDate(januaryFirst));
  }, []);

  const fetchFinancialReport = async () => {
    if (!selectedLocation || !startDate || !endDate) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get('/goat/report/financials', {
        params: {
          location_id: selectedLocation.id,
          start: startDate,
          end: endDate,
        },
      });
      setReport(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load financial report');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = async () => {
    if (!selectedLocation || !startDate || !endDate) return;
    
    try {
      // For now, just download as JSON. CSV export can be added later
      const csvContent = generateCSV(report!);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `goat_financial_report_${startDate}_${endDate}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const generateCSV = (report: GoatFinancialReport): string => {
    let csv = 'Goat Financial Report\n';
    csv += `Location: ${report.location_name}\n`;
    csv += `Period: ${report.period_start} to ${report.period_end}\n\n`;
    
    csv += 'Financial Summary\n';
    csv += `Total Income,${report.summary.total_income}\n`;
    csv += `Total Expenses,${report.summary.total_expenses}\n`;
    csv += `Location Expenses,${report.summary.total_location_expenses}\n`;
    csv += `Net Profit,${report.summary.net_profit}\n`;
    csv += `Profit Margin,${report.summary.profit_margin}%\n`;
    csv += `ROI,${report.summary.roi}%\n\n`;
    
    csv += 'Shed Profitability\n';
    csv += 'Shed,Income,Expenses,Location Costs,Net Profit,Margin%,ROI%,Goat Count,Cost/Goat,Revenue/Goat\n';
    report.shed_profitability.forEach(shed => {
      csv += `${shed.shed_name},${shed.total_income},${shed.total_expenses},${shed.allocated_location_costs},${shed.net_profit},${shed.profit_margin},${shed.roi},${shed.goat_count},${shed.cost_per_goat},${shed.revenue_per_goat}\n`;
    });
    
    return csv;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const getProfitColor = (profit: number) => {
    if (profit > 0) return 'text-green-600';
    if (profit < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getProfitIcon = (profit: number) => {
    if (profit > 0) return <ArrowTrendingUpIcon className="h-5 w-5 text-green-600" />;
    if (profit < 0) return <ArrowTrendingDownIcon className="h-5 w-5 text-red-600" />;
    return <ChartBarIcon className="h-5 w-5 text-gray-600" />;
  };

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <CurrencyDollarIcon className="h-16 w-16 text-green-600 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Goat Financial Reports</h1>
        <p className="text-lg text-gray-600">
          Profit & Loss Analysis and Financial Performance
        </p>
      </div>

      {/* Date Range Selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchFinancialReport}
              disabled={loading || !startDate || !endDate}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <CalendarIcon className="h-4 w-4" />
              {loading ? 'Loading...' : 'Generate Report'}
            </button>
            {report && (
              <button
                onClick={exportToCSV}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <DocumentArrowDownIcon className="h-4 w-4" />
                Export CSV
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {/* Financial Summary */}
      {report && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Financial Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(report.summary.total_income)}
              </div>
              <div className="text-sm text-gray-600">Total Income</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {formatCurrency(report.summary.total_expenses)}
              </div>
              <div className="text-sm text-gray-600">Total Expenses</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {formatCurrency(report.summary.total_location_expenses)}
              </div>
              <div className="text-sm text-gray-600">Location Costs</div>
              <div className="text-xs text-gray-500">Shared infrastructure</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${getProfitColor(report.summary.net_profit)}`}>
                {formatCurrency(report.summary.net_profit)}
              </div>
              <div className="text-sm text-gray-600">Net Profit</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${getProfitColor(report.summary.profit_margin)}`}>
                {formatPercentage(report.summary.profit_margin)}
              </div>
              <div className="text-sm text-gray-600">Profit Margin</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {report.shed_profitability.length}
              </div>
              <div className="text-sm text-gray-600">Active Sheds</div>
            </div>
          </div>
        </div>
      )}

      {/* Expense Breakdown */}
      {report && report.expense_breakdown.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Expense Breakdown</h2>
          <div className="space-y-4">
            {report.expense_breakdown.map((expense, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-medium text-gray-900 capitalize">
                    {expense.expense_type.replace('_', ' ')}
                  </h3>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-gray-900">
                      {formatCurrency(expense.total_amount)}
                    </div>
                    <div className="text-sm text-gray-600">
                      {formatPercentage(expense.percentage)} of total
                    </div>
                  </div>
                </div>
                {expense.pond_breakdown.length > 0 && (
                  <div className="mt-3">
                    <div className="text-sm text-gray-600 mb-2">Shed Breakdown:</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {expense.pond_breakdown.map((shed, shedIndex) => (
                        <div key={shedIndex} className="flex justify-between text-sm">
                          <span className="text-gray-700">{shed.pond_name}</span>
                          <span className="font-medium">{formatCurrency(shed.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Shed Profitability */}
      {report && report.shed_profitability.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Shed Profitability</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Shed
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Income
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expenses
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location Costs
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Allocation %
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Net Profit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Margin
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ROI
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Goats
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cost/Goat
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue/Goat
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {report.shed_profitability.map((shed, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {shed.shed_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {formatCurrency(shed.total_income)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                      {formatCurrency(shed.total_expenses)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-600">
                      <div>
                        <div className="font-medium">{formatCurrency(shed.allocated_location_costs)}</div>
                        <div className="text-xs text-gray-500">of {formatCurrency(shed.total_location_expenses)}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <span className="font-medium">{shed.allocation_proportion.toFixed(1)}%</span>
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-purple-600 h-2 rounded-full" 
                            style={{ width: `${Math.min(shed.allocation_proportion, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        {getProfitIcon(shed.net_profit)}
                        <span className={getProfitColor(shed.net_profit)}>
                          {formatCurrency(shed.net_profit)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={getProfitColor(shed.profit_margin)}>
                        {formatPercentage(shed.profit_margin)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={getProfitColor(shed.roi)}>
                        {formatPercentage(shed.roi)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {shed.goat_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatCurrency(shed.cost_per_goat)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatCurrency(shed.revenue_per_goat)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Monthly Trends */}
      {report && report.monthly_trends.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Monthly Trends</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Month
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Income
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expenses
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Profit
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {report.monthly_trends.map((month, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {month.month}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {formatCurrency(month.income)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                      {formatCurrency(month.expenses)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        {getProfitIcon(month.profit)}
                        <span className={getProfitColor(month.profit)}>
                          {formatCurrency(month.profit)}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoatFinancialReports;

